import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase/client';
import { applyBrandPalette, clearBrandPalette } from '@/lib/branding/palette';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
  brandLogo: string | null;
}

const ThemeContext = createContext<ThemeContextType>({
  theme: 'light',
  toggleTheme: () => {},
  setTheme: () => {},
  brandLogo: null,
});

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [brandLogo, setBrandLogo] = useState<string | null>(null);
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('fooddash-theme');
      if (stored === 'light' || stored === 'dark') return stored;
    }
    return 'light';
  });

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    localStorage.setItem('fooddash-theme', theme);
  }, [theme]);

  useEffect(() => {
    if (!user?.businessId) {
      clearBrandPalette();
      setBrandLogo(null);
      return;
    }
    let active = true;
    void supabase.from('businesses').select('primary_color,secondary_color,logo_url').eq('id', user.businessId).single().then(({ data }) => {
      if (!active || !data) return;
      applyBrandPalette(data.primary_color ?? '#3B82F6', data.secondary_color ?? '#F59E0B');
      setBrandLogo(data.logo_url);
    });
    return () => { active = false; };
  }, [user?.businessId]);

  useEffect(() => {
    const handleBrandingChange = (event: Event) => {
      const detail = (event as CustomEvent<{ primary: string; secondary: string; logo: string }>).detail;
      if (!detail) return;
      applyBrandPalette(detail.primary, detail.secondary);
      setBrandLogo(detail.logo || null);
    };
    window.addEventListener('fooddash:branding-changed', handleBrandingChange);
    return () => window.removeEventListener('fooddash:branding-changed', handleBrandingChange);
  }, []);

  const toggleTheme = () => {
    setThemeState((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme, brandLogo }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
